import { createSlice } from "@reduxjs/toolkit";
import { authenticate, deauthenticate, refreshToken } from "../services/auth";
import { extractErrorMessage } from "../lib/utils";
import { hostAgencyActions } from ".";

const initialState = {
  isLoading: false,
  error: null,
  currentUser: null,
};

const auth = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loginStarted: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    loginSuccess: (state, action) => {
      const { user, tokenExpiresAt } = action.payload;
      state.isLoading = false;
      state.currentUser = user;
      state.tokenExpiresAt = tokenExpiresAt;
      state.error = null;
    },
    loginFailed: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
      state.currentUser = null;
    },
    logOutFulfilled: (state) => {
      state.currentUser = null;
      state.tokenExpiresAt = null;
    },
    refreshStarted: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    refreshSuccess: (state, action) => {
      const { user, tokenExpiresAt } = action.payload;
      state.isLoading = false;
      state.currentUser = user;
      state.tokenExpiresAt = tokenExpiresAt;
      state.error = null;
    },
    refreshFailed: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
      state.currentUser = null;
    },
  },
});

// Thunk action: login
const login = (username, password) => async (dispatch) => {
  const { loginStarted, loginSuccess, loginFailed } = auth.actions;
  dispatch(loginStarted());

  return authenticate(username, password)
    .then(({ user, tokenExpiresAt }) =>
      dispatch(loginSuccess({ user, tokenExpiresAt }))
    )
    .catch((err) => {
      const message = extractErrorMessage(err);
      dispatch(loginFailed(message));
    });
};

// Thunk action: logout
const logout = () => async (dispatch) => {
  const { logOutFulfilled } = auth.actions;

  return deauthenticate()
    .then(() => {
      dispatch(logOutFulfilled());
      //put this in because we are clearing redux store upon logging out
      //host agency check will not have gone through, so page needs to refresh for host agency check to complete
    })
    .then(() => {
      dispatch(hostAgencyActions.fetch());
    });
};

// Thunk action: login
const refresh = (appointmentId) => async (dispatch) => {
  const { refreshStarted, refreshSuccess, refreshFailed } = auth.actions;
  dispatch(refreshStarted());

  return refreshToken(appointmentId)
    .then(({ user, tokenExpiresAt }) =>
      dispatch(refreshSuccess({ user, tokenExpiresAt }))
    )
    .catch((err) => {
      const message = extractErrorMessage(err);
      dispatch(refreshFailed(message));
    });
};

export const actions = { login, logout, refresh };

// selectors;
export const isLoggingIn = (state) => state.isLoading;
export const getCurrentUser = (state) => state.currentUser;
export const getCurrentUserRole = (state) =>
  state.currentUser && state.currentUser.role.toLowerCase();

// The current session is authenticated if current user is not empty and
// toekn is not expired yet.
export const isAuthenticated = (state) =>
  Boolean(state.currentUser) && new Date() < new Date(state.tokenExpiresAt);

export const getSessionExpirationTime = (state) => state.tokenExpiresAt;

// tell if the user's role matches with the provided role.
// the user is authorized if the provided role is empty, null or undefined
export const isAuthorized = (state, role) =>
  !role || getCurrentUserRole(state) === role;
export const getError = (state) => state.error;

export default auth.reducer;
